/**
 * Owns the socket for one live game of klaverjassen.
 *
 * Mirrors `useHeartsSession`, and shares `GameSocket` and the stored-session
 * helpers with it. What differs is the messages — naming a trump instead of
 * passing three cards — and which store they land in.
 */

import { useCallback, useEffect } from 'react';
import type { Card, Suit } from '@shared/types/game';
import type { ClientMessage } from '@shared/types/websocket';
import { GameSocket } from '@/live/socket';
import { rememberSeat, seatFor } from '@/live/sessions';
import { useKlaverjasLive } from '@/store/klaverjasLive';
import { useAuth } from '@/store/auth';

export interface KlaverjasActions {
  ready: (ready: boolean) => void;
  start: () => void;
  /** Null passes, where the rules allow it. */
  chooseTrump: (suit: Suit | null) => void;
  play: (card: Card) => void;
  /** Knock for the roem in the trick on the table — only with `roemClaim: zelf`. */
  claimRoem: () => void;
  leave: () => void;
}

export function useKlaverjasSession(
  code: string | undefined,
  profileId: string | null
): KlaverjasActions {
  const handle = useKlaverjasLive((state) => state.handle);
  const setStatus = useKlaverjasLive((state) => state.setStatus);
  const reset = useKlaverjasLive((state) => state.reset);

  useEffect(() => {
    if (!code) return;
    const upper = code.toUpperCase();

    const socket = new GameSocket(
      (message) => {
        if (message.type === 'joined') {
          rememberSeat({
            code: upper,
            mode: 'klaverjas',
            rejoinToken: message.rejoinToken,
            name: message.state.players[message.seat]?.name ?? '',
          });
        }
        handle(message);
      },
      setStatus,
      (): ClientMessage | null => {
        const stored = seatFor(upper);
        if (stored) {
          return {
            type: 'join',
            code: upper,
            playerName: stored.name,
            rejoinToken: stored.rejoinToken,
          };
        }
        /*
         * No profile means no seat: klaverjas rooms are seated when they are
         * made, so the name here is only there for the shared message shape and
         * the server ignores it entirely.
         */
        if (!profileId) return null;
        return { type: 'join', code: upper, playerName: '', profileId };
      },
      undefined,
      () => void useAuth.getState().refresh()
    );

    socket.connect();
    heldSocket = socket;

    return () => {
      socket.close();
      heldSocket = null;
      reset();
    };
  }, [code, profileId, handle, setStatus, reset]);

  const send = useCallback((message: ClientMessage) => heldSocket?.send(message), []);

  return {
    ready: useCallback((ready: boolean) => send({ type: 'ready', ready }), [send]),
    start: useCallback(() => send({ type: 'start-game' }), [send]),
    chooseTrump: useCallback((suit: Suit | null) => send({ type: 'choose-trump', suit }), [send]),
    play: useCallback((card: Card) => send({ type: 'play-card', card }), [send]),
    claimRoem: useCallback(() => send({ type: 'claim-roem' }), [send]),
    leave: useCallback(() => heldSocket?.close(), []),
  };
}

/** Module-level for the same reason as the other two sessions: one table per tab. */
let heldSocket: GameSocket | null = null;
